import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useList } from '@/hooks/resourceQueries';
import { useTranslation } from 'react-i18next';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PeriodInput } from '@/components/ui/period-input';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogFooter
} from '@/components/ui/dialog';
import {
	Calendar,
	Clock,
	Users,
	Car,
	Building,
	Plus,
	Save,
	Edit,
	AlertCircle
} from 'lucide-react';
import {
	VEHICLE_TYPES_QUERY,
	CUSTOMER_CATEGORIES_QUERY,
	WEEKLY_DATE_SETS_QUERY,
	PAYMENT_METHODS_QUERY
} from './scenarios.gql';
import type {
	VehicleTypeViewModel,
	CustomerCategoryViewModel,
	WeeklyDateSetViewModel,
	PaymentMethodViewModel
} from '@/graphql/generated';
import { validateConditionHasActions } from '@/lib/conditionValidation';
import { toast } from 'sonner';

interface ConditionFormData {
	startDate?: string;
	endDate?: string;
	arrivingSince?: string;
	arrivingUntil?: string;
	leavingSince?: string;
	leavingUntil?: string;
	weeklyDateSetId?: number;
	appliesOnHolidays: boolean;
	minStayMinutes?: number;
	maxStayMinutes?: number;
	appliesToSpecificVehicleTypes: boolean;
	appliesToSpecificCustomerCategories: boolean;
	appliesToSpecificPaymentMethods: boolean;
	isOverstay: boolean;
	isContractOverstay: boolean;
	selectedVehicleTypes: string[];
	selectedCustomerCategories: string[];
	selectedPaymentMethods: string[];
	selectedDaysOfWeek: string[];
}

interface ConditionEditorProps {
	condition?: ConditionFormData;
	operator: 'include' | 'exclude';
	onSave: (
		condition: ConditionFormData & { operator: 'include' | 'exclude' }
	) => void;
	onCancel: () => void;
	open: boolean;
	mode: 'create' | 'edit';
}

// Days of week will be translated in the component using t()
const DAYS_OF_WEEK_IDS = [
	{ id: '1', key: 'monday' },
	{ id: '2', key: 'tuesday' },
	{ id: '3', key: 'wednesday' },
	{ id: '4', key: 'thursday' },
	{ id: '5', key: 'friday' },
	{ id: '6', key: 'saturday' },
	{ id: '7', key: 'sunday' }
];

export const ConditionEditor: React.FC<ConditionEditorProps> = ({
	condition,
	operator,
	onSave,
	onCancel,
	open,
	mode
}) => {
	const { t } = useTranslation('scenarios');
	const [activeTab, setActiveTab] = useState<
		'basic' | 'time' | 'date' | 'stay' | 'restrictions'
	>('basic');

	const { control, handleSubmit, reset, watch, setValue, setError, formState: { errors } } =
		useForm<ConditionFormData>({
			defaultValues: {
				appliesOnHolidays: false,
				appliesToSpecificVehicleTypes: false,
				appliesToSpecificCustomerCategories: false,
				appliesToSpecificPaymentMethods: false,
				isOverstay: false,
				isContractOverstay: false,
				selectedVehicleTypes: [],
				selectedCustomerCategories: [],
				selectedPaymentMethods: [],
				selectedDaysOfWeek: []
			}
		});

	// Load data for dropdowns
	const { data: vehicleTypesData } = useList<VehicleTypeViewModel>({
		resource: 'vehicleTypes',
		meta: {
			gqlQuery: VEHICLE_TYPES_QUERY
		}
	});

	const { data: customerCategoriesData } = useList<CustomerCategoryViewModel>(
		{
			resource: 'customerCategories',
			meta: {
				gqlQuery: CUSTOMER_CATEGORIES_QUERY
			}
		}
	);

	const { data: weeklyDateSetsData } = useList<WeeklyDateSetViewModel>({
		resource: 'weeklyDateSets',
		meta: {
			gqlQuery: WEEKLY_DATE_SETS_QUERY
		}
	});

	const { data: paymentMethodsData } = useList<PaymentMethodViewModel>({
		resource: 'paymentMethods',
		meta: {
			gqlQuery: PAYMENT_METHODS_QUERY
		}
	});

	const vehicleTypes = vehicleTypesData?.data || [];
	const customerCategories = customerCategoriesData?.data || [];
	const weeklyDateSets = weeklyDateSetsData?.data || [];
	const paymentMethods = paymentMethodsData?.data || [];

	const weeklyDateSetId = watch('weeklyDateSetId');
	const selectedWeeklyDateSet = weeklyDateSets.find(
		set => set.weeklyDateSetId === weeklyDateSetId
	);
	const showCustomWeekdays =
		selectedWeeklyDateSet?.name?.toLowerCase() === 'custom';

	const selectedDaysOfWeek = watch('selectedDaysOfWeek');

	useEffect(() => {
		if (weeklyDateSetId != null || weeklyDateSets.length === 0) {
			return;
		}

		const normaliseName = (value?: string | null) =>
			value?.replace(/\s+/g, '').toLowerCase();

		const defaultWeeklySet =
			weeklyDateSets.find(set => normaliseName(set.name) === 'alldays') ??
			weeklyDateSets[0];

		if (defaultWeeklySet) {
			setValue('weeklyDateSetId', defaultWeeklySet.weeklyDateSetId);
		}
	}, [weeklyDateSetId, weeklyDateSets, setValue]);

	useEffect(() => {
		if (!showCustomWeekdays && selectedDaysOfWeek.length > 0) {
			setValue('selectedDaysOfWeek', []);
		}
	}, [showCustomWeekdays, selectedDaysOfWeek, setValue]);

	// No longer need to watch these since we always show restriction options

	// Populate form when editing existing condition or reset for new condition
	useEffect(() => {
		if (open) {
			// Reset active tab when dialog opens
			setActiveTab('basic');

			if (condition && mode === 'edit') {
				reset({
					startDate: condition.startDate
						? new Date(condition.startDate)
								.toISOString()
								.split('T')[0]
						: undefined,
					endDate: condition.endDate
						? new Date(condition.endDate)
								.toISOString()
								.split('T')[0]
						: undefined,
					arrivingSince: condition.arrivingSince || undefined,
					arrivingUntil: condition.arrivingUntil || undefined,
					leavingSince: condition.leavingSince || undefined,
					leavingUntil: condition.leavingUntil || undefined,
					weeklyDateSetId: condition.weeklyDateSetId || undefined,
					appliesOnHolidays: condition.appliesOnHolidays || false,
					minStayMinutes: condition.minStayMinutes || undefined,
					maxStayMinutes: condition.maxStayMinutes || undefined,
					appliesToSpecificVehicleTypes:
						condition.appliesToSpecificVehicleTypes || false,
					appliesToSpecificCustomerCategories:
						condition.appliesToSpecificCustomerCategories || false,
					appliesToSpecificPaymentMethods:
						condition.appliesToSpecificPaymentMethods || false,
					isOverstay: condition.isOverstay || false,
					isContractOverstay: condition.isContractOverstay || false,
					selectedVehicleTypes: condition.selectedVehicleTypes || [],
					selectedCustomerCategories:
						condition.selectedCustomerCategories || [],
					selectedPaymentMethods:
						condition.selectedPaymentMethods || [],
					selectedDaysOfWeek: condition.selectedDaysOfWeek || []
				});
			} else if (mode === 'create') {
				reset({
					appliesOnHolidays: false,
					appliesToSpecificVehicleTypes: false,
					appliesToSpecificCustomerCategories: false,
					appliesToSpecificPaymentMethods: false,
					isOverstay: false,
					isContractOverstay: false,
					selectedVehicleTypes: [],
					selectedCustomerCategories: [],
					selectedPaymentMethods: [],
					selectedDaysOfWeek: []
				});
			}
		}
	}, [open, condition, mode]);

	const onSubmit = (data: ConditionFormData) => {
		// Validate that minStayMinutes is less than maxStayMinutes when both are set
		if (data.minStayMinutes && data.maxStayMinutes && data.minStayMinutes >= data.maxStayMinutes) {
			setError('minStayMinutes', {
				type: 'manual',
				message: t('conditionEditor.minStayMustBeLessThanMax')
			});
			setActiveTab('stay');
			return;
		}

		// Automatically set the "applies to specific" flags based on selections
		const processedData = {
			...data,
			operator,
			// Auto-set flags based on whether any items are selected
			appliesToSpecificVehicleTypes: data.selectedVehicleTypes.length > 0,
			appliesToSpecificCustomerCategories:
				data.selectedCustomerCategories.length > 0,
			appliesToSpecificPaymentMethods:
				data.selectedPaymentMethods.length > 0
		};

		// Validate that the condition has meaningful actions
		if (!validateConditionHasActions(processedData)) {
			toast.error(t('conditionEditor.validationError'), {
				duration: 5000
			});
			return;
		}

		onSave(processedData);
	};

	const tabs = [
		{
			id: 'basic',
			label: t('conditionEditor.generalRulesTab'),
			icon: Building
		},
		{ id: 'time', label: t('conditionEditor.timeRulesTab'), icon: Clock },
		{
			id: 'date',
			label: t('conditionEditor.dateRulesTab'),
			icon: Calendar
		},
		{
			id: 'stay',
			label: t('conditionEditor.stayRulesTab'),
			icon: AlertCircle
		},
		{
			id: 'restrictions',
			label: t('conditionEditor.restrictionsTab'),
			icon: Users
		}
	];

	return (
		<Dialog
			open={open}
			onOpenChange={isOpen => {
				if (!isOpen) onCancel();
			}}
		>
			<DialogContent
				className="sm:w-[80vw] sm:max-w-[1000px] sm:max-h-[85vh] overflow-hidden flex flex-col p-0 sm:p-0"
				size="lg"
				enablePreRender={true}
			>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col h-full min-h-0"
				>
					<DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2">
						<div>
							<DialogTitle className="flex items-center gap-2">
								{mode === 'create' ? (
									<Plus className="w-5 h-5" />
								) : (
									<Edit className="w-5 h-5" />
								)}
								{mode === 'create'
									? t('conditionEditor.configureTitle')
									: t('conditionEditor.configureTitle')}
							</DialogTitle>
							<DialogDescription>
								{t('conditionEditor.configureDesc')}
							</DialogDescription>
						</div>
					</DialogHeader>

					<div className="flex flex-col flex-1 min-h-0 px-6 overflow-hidden">
						{/* Tabs */}
						<div className="flex flex-wrap flex-shrink-0 gap-1 pt-6 mb-6 overflow-y-auto rounded-lg bg-background max-h-24">
							{tabs.map(tab => {
								const Icon = tab.icon;
								return (
									<button
										key={tab.id}
										type="button"
										onClick={() =>
											setActiveTab(tab.id as any)
										}
										className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors flex-shrink-0 justify-center whitespace-nowrap ${
											activeTab === tab.id
												? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
												: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
										}`}
									>
										<Icon className="w-4 h-4" />
										{tab.label}
									</button>
								);
							})}
						</div>

						{/* Tab Content */}
						<div className="flex-1 min-h-0 overflow-y-auto">
							<div className="pb-6 space-y-6">
								{/* Basic Info Tab */}
								{activeTab === 'basic' && (
									<Card>
										<CardHeader>
											<CardTitle className="flex items-center gap-2">
												<Building className="w-5 h-5" />
												{t(
													'conditionEditor.generalRuleOptions'
												)}
											</CardTitle>
											<CardDescription>
												{t(
													'conditionEditor.generalRuleOptionsDesc'
												)}
											</CardDescription>
										</CardHeader>
										<CardContent className="space-y-4">
											<div className="space-y-4">
												<Label>
													{t(
														'conditionEditor.generalOptions'
													)}
												</Label>
												<div className="space-y-3">
													<Controller
														name="appliesOnHolidays"
														control={control}
														render={({ field }) => (
															<div className="flex items-center space-x-2">
																<Checkbox
																	id="appliesOnHolidays"
																	checked={
																		field.value
																	}
																	onCheckedChange={
																		field.onChange
																	}
																/>
																<Label htmlFor="appliesOnHolidays">
																	{t(
																		'conditionEditor.appliesOnHolidays'
																	)}
																</Label>
															</div>
														)}
													/>

													<Controller
														name="isOverstay"
														control={control}
														render={({ field }) => (
															<div className="flex items-center space-x-2">
																<Checkbox
																	id="isOverstay"
																	checked={
																		field.value
																	}
																	onCheckedChange={
																		field.onChange
																	}
																/>
																<Label htmlFor="isOverstay">
																	{t(
																		'conditionEditor.isOverstayCondition'
																	)}
																</Label>
															</div>
														)}
													/>

													<Controller
														name="isContractOverstay"
														control={control}
														render={({ field }) => (
															<div className="flex items-center space-x-2">
																<Checkbox
																	id="isContractOverstay"
																	checked={
																		field.value
																	}
																	onCheckedChange={
																		field.onChange
																	}
																/>
																<Label htmlFor="isContractOverstay">
																	{t(
																		'conditionEditor.isContractOverstay'
																	)}
																</Label>
															</div>
														)}
													/>
												</div>
											</div>
										</CardContent>
									</Card>
								)}

								{/* Time Rules Tab */}
								{activeTab === 'time' && (
									<Card>
										<CardHeader>
											<CardTitle className="flex items-center gap-2">
												<Clock className="w-5 h-5" />
												Time Rules
											</CardTitle>
											<CardDescription>
												t('conditionEditor.timeRulesDesc')
											</CardDescription>
										</CardHeader>
										<CardContent className="space-y-6">
											<div className="grid grid-cols-2 gap-4">
												<div className="space-y-2">
													<Label htmlFor="arrivingSince">
														Arriving Since
													</Label>
													<Controller
														name="arrivingSince"
														control={control}
														render={({ field }) => (
															<Input
																{...field}
																id="arrivingSince"
																type="time"
																placeholder="HH:MM"
															/>
														)}
													/>
												</div>
												<div className="space-y-2">
													<Label htmlFor="arrivingUntil">
														Arriving Until
													</Label>
													<Controller
														name="arrivingUntil"
														control={control}
														render={({ field }) => (
															<Input
																{...field}
																id="arrivingUntil"
																type="time"
																placeholder="HH:MM"
															/>
														)}
													/>
												</div>
											</div>

											<Separator />

											<div className="grid grid-cols-2 gap-4">
												<div className="space-y-2">
													<Label htmlFor="leavingSince">
														Leaving Since
													</Label>
													<Controller
														name="leavingSince"
														control={control}
														render={({ field }) => (
															<Input
																{...field}
																id="leavingSince"
																type="time"
																placeholder="HH:MM"
															/>
														)}
													/>
												</div>
												<div className="space-y-2">
													<Label htmlFor="leavingUntil">
														{t(
															'conditionEditor.leavingUntil'
														)}
													</Label>
													<Controller
														name="leavingUntil"
														control={control}
														render={({ field }) => (
															<Input
																{...field}
																id="leavingUntil"
																type="time"
																placeholder="HH:MM"
															/>
														)}
													/>
												</div>
											</div>
										</CardContent>
									</Card>
								)}

								{/* Date Rules Tab */}
								{activeTab === 'date' && (
									<Card>
										<CardHeader>
											<CardTitle className="flex items-center gap-2">
												<Calendar className="w-5 h-5" />
												Date Rules
											</CardTitle>
											<CardDescription>
												t('conditionEditor.dateRulesDesc')
											</CardDescription>
										</CardHeader>
										<CardContent className="space-y-6">
											<div className="grid grid-cols-2 gap-4">
												<div className="space-y-2">
													<Label htmlFor="startDate">
														Start Date
													</Label>
													<Controller
														name="startDate"
														control={control}
														render={({ field }) => (
															<Input
																{...field}
																id="startDate"
																type="date"
															/>
														)}
													/>
												</div>
												<div className="space-y-2">
													<Label htmlFor="endDate">
														End Date
													</Label>
													<Controller
														name="endDate"
														control={control}
														render={({ field }) => (
															<Input
																{...field}
																id="endDate"
																type="date"
															/>
														)}
													/>
												</div>
											</div>

											<Separator />

											<div className="space-y-2">
												<Label htmlFor="weeklyDateSetId">
													Weekly Date Set
												</Label>
												<Controller
													name="weeklyDateSetId"
													control={control}
													render={({ field }) => (
														<SentryDropdown
															options={weeklyDateSets.map(
																wds => ({
																	value: wds.weeklyDateSetId.toString(),
																	label: wds.name
																})
															)}
															value={
																field.value?.toString() ||
																''
															}
															onChange={value =>
																field.onChange(
																	parseInt(
																		value as string
																	)
																)
															}
															placeholder="t('conditionEditor.selectWeeklyDateSet')"
															searchable
															className="w-full"
														/>
													)}
												/>
											</div>

											<Separator />

											{showCustomWeekdays && (
												<div className="space-y-3">
													<Label>
														{t(
															'conditionEditor.daysOfWeek'
														)}
													</Label>
													<div className="grid grid-cols-4 gap-2">
														{DAYS_OF_WEEK_IDS.map(
															day => (
																<Controller
																	key={day.id}
																	name="selectedDaysOfWeek"
																	control={
																		control
																	}
																	render={({
																		field
																	}) => (
																		<div className="flex items-center space-x-2">
																			<Checkbox
																				id={`day-${day.id}`}
																				checked={field.value.includes(
																					day.id
																				)}
																				onCheckedChange={checked => {
																					if (
																						checked
																					) {
																						field.onChange(
																							[
																								...field.value,
																								day.id
																							]
																						);
																					} else {
																						field.onChange(
																							field.value.filter(
																								id =>
																									id !==
																									day.id
																							)
																						);
																					}
																				}}
																			/>
																			<Label
																				htmlFor={`day-${day.id}`}
																				className="text-sm"
																			>
																				{t(
																					`conditionEditor.${day.key}`
																				).slice(
																					0,
																					3
																				)}
																			</Label>
																		</div>
																	)}
																/>
															)
														)}
													</div>
												</div>
											)}
										</CardContent>
									</Card>
								)}

								{/* Stay Rules Tab */}
								{activeTab === 'stay' && (
									<Card>
										<CardHeader>
											<CardTitle className="flex items-center gap-2">
												<AlertCircle className="w-5 h-5" />
												Stay Duration Rules
											</CardTitle>
											<CardDescription>
												Configure minimum and maximum
												stay duration limits
											</CardDescription>
										</CardHeader>
										<CardContent className="space-y-4">
<div className="grid grid-cols-2 gap-4">
														<div className="space-y-2">
															<Label htmlFor="minStayMinutes">
																{t('conditionEditor.minimumStay')}
															</Label>
															<Controller
																name="minStayMinutes"
																control={control}
																render={({ field }) => (
																	<PeriodInput
																		value={field.value}
																		onChange={field.onChange}
																		aria-invalid={!!errors.minStayMinutes}
																	/>
																)}
															/>
														</div>
														<div className="space-y-2">
															<Label htmlFor="maxStayMinutes">
																{t('conditionEditor.maximumStay')}
															</Label>
															<Controller
																name="maxStayMinutes"
																control={control}
																render={({ field }) => (
																	<PeriodInput
																		value={field.value}
																		onChange={field.onChange}
																	/>
																)}
															/>
														</div>
													</div>
													{errors.minStayMinutes && (
														<p className="text-sm text-destructive">
															{errors.minStayMinutes.message}
														</p>
													)}
										</CardContent>
									</Card>
								)}

								{/* Restrictions Tab */}
								{activeTab === 'restrictions' && (
									<Card>
										<CardHeader>
											<CardTitle className="flex items-center gap-2">
												<Users className="w-5 h-5" />
												Vehicle & Customer Restrictions
											</CardTitle>
											<CardDescription>
												t('conditionEditor.vehicleCustomerRestrictionsDesc')
											</CardDescription>
										</CardHeader>
										<CardContent className="space-y-6">
											{/* Vehicle Types */}
											<div className="space-y-4">
												<Label className="text-base font-medium">
													Vehicle Types
												</Label>
												<p className="text-sm text-muted-foreground">
													t('conditionEditor.vehicleTypesDesc')
												</p>
												<div className="grid grid-cols-2 gap-2 overflow-y-auto max-h-40">
													{vehicleTypes.map(vt => (
														<Controller
															key={
																vt.vehicleTypeId
															}
															name="selectedVehicleTypes"
															control={control}
															render={({
																field
															}) => (
																<div className="flex items-center space-x-2">
																	<Checkbox
																		id={`vt-${vt.vehicleTypeId}`}
																		checked={field.value.includes(
																			vt.vehicleTypeId.toString()
																		)}
																		onCheckedChange={checked => {
																			if (
																				checked
																			) {
																				field.onChange(
																					[
																						...field.value,
																						vt.vehicleTypeId.toString()
																					]
																				);
																			} else {
																				field.onChange(
																					field.value.filter(
																						id =>
																							id !==
																							vt.vehicleTypeId.toString()
																					)
																				);
																			}
																		}}
																	/>
																	<Label
																		htmlFor={`vt-${vt.vehicleTypeId}`}
																		className="text-sm"
																	>
																		<div className="flex items-center gap-2">
																			<Car className="w-3 h-3" />
																			{
																				vt.name
																			}
																		</div>
																	</Label>
																</div>
															)}
														/>
													))}
												</div>
											</div>

											<Separator />

											{/* Customer Categories */}
											<div className="space-y-4">
												<Label className="text-base font-medium">
													Customer Categories
												</Label>
												<p className="text-sm text-muted-foreground">
													Select customer categories
													to restrict this condition
													to, or leave all unchecked
													to apply to all customers.
												</p>
												<div className="grid grid-cols-1 gap-2 overflow-y-auto max-h-40">
													{customerCategories.map(
														cc => (
															<Controller
																key={
																	cc.customerCategoryId
																}
																name="selectedCustomerCategories"
																control={
																	control
																}
																render={({
																	field
																}) => (
																	<div className="flex items-center space-x-2">
																		<Checkbox
																			id={`cc-${cc.customerCategoryId}`}
																			checked={field.value.includes(
																				cc.customerCategoryId.toString()
																			)}
																			onCheckedChange={checked => {
																				if (
																					checked
																				) {
																					field.onChange(
																						[
																							...field.value,
																							cc.customerCategoryId.toString()
																						]
																					);
																				} else {
																					field.onChange(
																						field.value.filter(
																							id =>
																								id !==
																								cc.customerCategoryId.toString()
																						)
																					);
																				}
																			}}
																		/>
																		<Label
																			htmlFor={`cc-${cc.customerCategoryId}`}
																			className="text-sm"
																		>
																			<div className="flex items-center gap-2">
																				<Users className="w-3 h-3" />
																				{
																					cc.description
																				}
																			</div>
																		</Label>
																	</div>
																)}
															/>
														)
													)}
												</div>
											</div>

											<Separator />

											{/* Payment Methods */}
											<div className="space-y-4">
												<Label className="text-base font-medium">
													Payment Methods
												</Label>
												<p className="text-sm text-muted-foreground">
													t('conditionEditor.paymentMethodsDesc')
												</p>
												<div className="grid grid-cols-1 gap-2 overflow-y-auto max-h-40">
													{paymentMethods.map(pm => (
														<Controller
															key={
																pm.paymentMethodId
															}
															name="selectedPaymentMethods"
															control={control}
															render={({
																field
															}) => (
																<div className="flex items-center space-x-2">
																	<Checkbox
																		id={`pm-${pm.paymentMethodId}`}
																		checked={field.value.includes(
																			pm.paymentMethodId.toString()
																		)}
																		onCheckedChange={checked => {
																			if (
																				checked
																			) {
																				field.onChange(
																					[
																						...field.value,
																						pm.paymentMethodId.toString()
																					]
																				);
																			} else {
																				field.onChange(
																					field.value.filter(
																						id =>
																							id !==
																							pm.paymentMethodId.toString()
																					)
																				);
																			}
																		}}
																	/>
																	<Label
																		htmlFor={`pm-${pm.paymentMethodId}`}
																		className="text-sm"
																	>
																		<div className="flex items-center gap-2">
																			<Building className="w-3 h-3" />
																			{
																				pm.name
																			}
																		</div>
																	</Label>
																</div>
															)}
														/>
													))}
												</div>
											</div>
										</CardContent>
									</Card>
								)}
							</div>
						</div>
					</div>

					<DialogFooter className="flex justify-between flex-shrink-0 px-6 pt-4 pb-6 mt-4 border-t">
						<Button
							type="button"
							variant="outline"
							onClick={onCancel}
						>
							{t('conditionEditor.cancel')}
						</Button>
						<Button type="submit">
							<Save className="w-4 h-4 mr-2" />
							{mode === 'create'
								? t('conditionEditor.saveRules')
								: t('conditionEditor.updateRules')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
